import {ShaderChunk,Matrix3,Matrix4,Vector3,Color} from 'three';
import {variantMaterial,variantMood} from './variants.js';
// These are art-direction values, not measurements of real squishy materials.
const JELLY = {
  jelly_watermelon: {color:'#ff6070',absorption:'#d91d36',transmission:.68,thickness:.7},
  jelly_peach: {color:'#ffd2b4',absorption:'#ff784e',transmission:.76,thickness:.65},
  jelly_star: {color:'#fff0a3',absorption:'#e8a52c',transmission:.8,thickness:.55},
  jelly_heart: {color:'#ff9dbd',absorption:'#e84076',transmission:.75,thickness:.65},
  jelly_grape: {color:'#be91ec',absorption:'#7730b6',transmission:.62,thickness:.5},
  jelly_sugar: {color:'#fff1cc',absorption:'#e3a14a',transmission:.95,thickness:.95},
  jelly_bun_milk: {color:'#fff8f0',absorption:'#efd6c2',transmission:.66,thickness:.8},
  jelly_octopus: {color:'#ffc4d6',absorption:'#ff5f8f',transmission:.66,thickness:.7},
  jelly_mikan: {color:'#ffb24d',absorption:'#ff7a12',transmission:.42,thickness:.9},
  jelly_egg_yolk: {color:'#ffb21f',absorption:'#ff7a00',transmission:.3,thickness:.8},
};

// A CSS filter list as one 3x3 colour matrix (column-major, for GLSL mat3),
// using the Filter Effects matrices for hue-rotate, saturate, sepia,
// brightness and grayscale. Rare colours with a thumbnail filter apply the
// same matrix to their vertex-coloured or textured material in the shader.
export function cssFilterMatrix(css){
  let m=[1,0,0,0,1,0,0,0,1];
  const times=(a,b)=>{const out=new Array(9);for(let c=0;c<3;c++)for(let r=0;r<3;r++)out[c*3+r]=a[r]*b[c*3]+a[3+r]*b[c*3+1]+a[6+r]*b[c*3+2];return out;};
  const rows=(...v)=>[v[0],v[3],v[6],v[1],v[4],v[7],v[2],v[5],v[8]];
  for(const [,name,raw,unit] of String(css??'').matchAll(/([a-z-]+)\(\s*(-?[\d.]+)\s*(deg|%)?\s*\)/g)){
    let v=Number(raw);if(unit==='%')v/=100;let f=null;
    if(name==='hue-rotate'){const a=v*Math.PI/180,c=Math.cos(a),s=Math.sin(a);
      f=rows(.213+c*.787-s*.213,.715-c*.715-s*.715,.072-c*.072+s*.928,.213-c*.213+s*.143,.715+c*.285+s*.140,.072-c*.072-s*.283,.213-c*.213-s*.787,.715-c*.715+s*.715,.072+c*.928+s*.072);}
    else if(name==='saturate')f=rows(.213+.787*v,.715-.715*v,.072-.072*v,.213-.213*v,.715+.285*v,.072-.072*v,.213-.213*v,.715-.715*v,.072+.928*v);
    else if(name==='grayscale'){const g=1-Math.min(1,v);f=rows(.2126+.7874*g,.7152-.7152*g,.0722-.0722*g,.2126-.2126*g,.7152+.2848*g,.0722-.0722*g,.2126-.2126*g,.7152-.7152*g,.0722+.9278*g);}
    else if(name==='sepia'){const p=Math.min(1,v),q=1-p;f=rows(.393+.607*q,.769-.769*q,.189-.189*q,.349-.349*q,.686+.314*q,.168-.168*q,.272-.272*q,.534-.534*q,.131+.869*q);}
    else if(name==='brightness')f=[v,0,0,0,v,0,0,0,v];
    if(f)m=times(f,m);
  }
  return m;
}
// Browning by heat: a per-vertex toast level turns the mochi golden, then adds
// darker Maillard spots from a solid noise in rest space. With crust, a puff's
// skin cracks as it stretches (skinStretch, area over rest area): the toasted
// crust breaks into flakes with darker edges, and fresh white mochi shows
// between them. The same rest-space noise keeps the puff's rim seamless with
// the crust around it.
export function mochiToastShader(shader,{crust=false}={}){
  shader.vertexShader='attribute float toastLevel;\nattribute vec3 restPosition;\nvarying float vToast;\nvarying vec3 vToastRest;\n'+(crust?'attribute float skinStretch;\nvarying float vStretch;\n':'')+
    shader.vertexShader.replace('#include <begin_vertex>','#include <begin_vertex>\n  vToast=toastLevel;vToastRest=restPosition;'+(crust?'vStretch=skinStretch;':''));
  let fragment=`varying float vToast;varying vec3 vToastRest;${crust?'varying float vStretch;':''}
    float toastHash(vec3 p){p=fract(p*.3183099+.1);p*=17.;return fract(p.x*p.y*p.z*(p.x+p.y+p.z));}
    float toastNoise(vec3 x){vec3 i=floor(x),f=fract(x);f=f*f*(3.-2.*f);
      return mix(mix(mix(toastHash(i),toastHash(i+vec3(1,0,0)),f.x),mix(toastHash(i+vec3(0,1,0)),toastHash(i+vec3(1,1,0)),f.x),f.y),
        mix(mix(toastHash(i+vec3(0,0,1)),toastHash(i+vec3(1,0,1)),f.x),mix(toastHash(i+vec3(0,1,1)),toastHash(i+vec3(1,1,1)),f.x),f.y),f.z);}
    ${crust?`// Crust plates: nearest and second-nearest cell distances; their
    // difference is small near a plate's border.
    vec2 crustCells(vec3 p){vec3 i=floor(p),f=fract(p);float f1=8.,f2=8.;
      for(int z=-1;z<=1;z++)for(int y=-1;y<=1;y++)for(int x=-1;x<=1;x++){
        vec3 g=vec3(x,y,z),r=g+vec3(toastHash(i+g),toastHash(i+g+vec3(17.3,4.1,9.7)),toastHash(i+g+vec3(5.9,23.3,1.7)))-f;float d=dot(r,r);
        if(d<f1){f2=f1;f1=d;}else if(d<f2)f2=d;}
      return sqrt(vec2(f1,f2));}
    float mochiGap=0.;`:''}
  `+shader.fragmentShader.replace('#include <color_fragment>',`#include <color_fragment>
    float toast=vToast,edge=0.;
    ${crust?`// As the skin stretches, the dry crust splits into plates and fresh
    // white mochi swells up between them; the plate edges lift and darken.
    if(vStretch>1.01){
      vec2 cells=crustCells(vToastRest*15.);float border=cells.y-cells.x,width=clamp((vStretch-1.)*.4,0.,.46);
      mochiGap=1.-smoothstep(width-.03,width+.01,border);
      edge=smoothstep(width,width+.03,border)*(1.-smoothstep(width+.03,width+.08,border))*smoothstep(.02,.1,width);
      toast*=1.-mochiGap;
    }`:''}
    float toastSpots=toastNoise(vToastRest*9.)*.65+toastNoise(vToastRest*23.)*.35;
    diffuseColor.rgb=mix(diffuseColor.rgb,vec3(.75,.40,.11),smoothstep(.12,.65,toast)*.85);
    diffuseColor.rgb=mix(diffuseColor.rgb,vec3(.30,.11,.03),smoothstep(.5,1.,toast)*smoothstep(.45,.72,toastSpots)*.8);
    ${crust?`diffuseColor.rgb=mix(diffuseColor.rgb,vec3(1.,.99,.97),mochiGap*.55);
    diffuseColor.rgb*=1.-edge*(.12+.3*smoothstep(.1,.5,vToast));`:''}`);
  // Fresh mochi in the gaps is moist and glossier than the dry crust.
  if(crust)fragment=fragment.replace('#include <roughnessmap_fragment>','#include <roughnessmap_fragment>\n    roughnessFactor=mix(roughnessFactor,.3,mochiGap);');
  shader.fragmentShader=fragment;
}
export function finishToyMaterial(mat,studio=null,bounds=null,{kind,mobile=false,variant}={}){
  const recolor=variantMaterial(kind,variant,mat.name);
  if(!mat.isMeshPhysicalMaterial)return mat;
  if(['jelly_amber_honey','jelly_water_clear'].includes(mat.name)){
    if(studio)mat.envMap=studio;
    mat.envMapIntensity=mat.name==='jelly_water_clear'?1.15:.85;
    mat.clearcoat=.32;mat.clearcoatRoughness=.06;
    // The screen-space refraction buffer contains only the pale page behind
    // these toys. Add the surrounding studio at the refracted ray direction;
    // retain the screen sample so internal fruit and bubbles remain visible.
    const studioBlend=mat.name==='jelly_water_clear'?'0.86':'0.78';
    const water=mat.name==='jelly_water_clear';
    mat.customProgramCacheKey=()=> 'studio-volume-v6-'+studioBlend;
    const volume={inverse:{value:new Matrix4()},center:{value:bounds?.getCenter(new Vector3())??new Vector3()},radius:{value:bounds?.getSize(new Vector3()).multiplyScalar(.5)??new Vector3(1,1,1)}};
    if(water&&bounds){volume.center.value.y=bounds.min.y;volume.radius.value.y=bounds.max.y-bounds.min.y;}
    mat.userData.updateVolume=(matrix,currentBounds)=>{
      volume.inverse.value.copy(matrix).invert();
      if(currentBounds){
        currentBounds.getCenter(volume.center.value);currentBounds.getSize(volume.radius.value).multiplyScalar(.5);
        if(water){volume.center.value.y=currentBounds.min.y;volume.radius.value.y=currentBounds.max.y-currentBounds.min.y;}
      }
    };
    mat.onBeforeCompile=shader=>{
      shader.uniforms.studioInverse=volume.inverse;shader.uniforms.studioCenter=volume.center;shader.uniforms.studioRadius=volume.radius;
      shader.fragmentShader='uniform mat4 studioInverse;\nuniform vec3 studioCenter;\nuniform vec3 studioRadius;\n'+shader.fragmentShader;
      shader.fragmentShader=shader.fragmentShader.replace('#include <transmission_pars_fragment>',
        ShaderChunk.transmission_pars_fragment.replace('vec3 attenuatedColor = transmittance * transmittedLight.rgb;', `
          #ifdef ENVMAP_TYPE_CUBE_UV
            vec3 insideRay = refract(-v, n, 1.0 / ior);
            vec3 localOrigin = (studioInverse * vec4(position, 1.0)).xyz - studioCenter;
            vec3 localRay = (studioInverse * vec4(insideRay, 0.0)).xyz;
            vec3 safeRadius = max(studioRadius, vec3(0.01));
            vec3 o = localOrigin / safeRadius, d = localRay / safeRadius;
            float aa = dot(d,d), bb = dot(o,d), cc = dot(o,o)-1.0;
            float proxyDiscriminant=bb*bb-aa*cc;
            float travel = max(0.0, (-bb + sqrt(max(0.0,proxyDiscriminant))) / max(aa,0.00001));
            // A hexagonal corner can miss the smooth proxy. Preserve the
            // original refraction instead of inventing an exit surface.
            if(proxyDiscriminant>=0.0 && travel>0.0001){
            vec3 exitLocalNormal = normalize((localOrigin + localRay * travel) / (safeRadius * safeRadius));
            ${water?`float floorTravel=localRay.y < -0.00001 ? -localOrigin.y/localRay.y : 1e6;
            if(floorTravel>0.0 && floorTravel<travel){travel=floorTravel;exitLocalNormal=vec3(0.0,-1.0,0.0);}`:''}
            vec3 exitNormal = normalize(transpose(mat3(studioInverse)) * exitLocalNormal);
            vec3 studioRay = refract(insideRay, -exitNormal, ior);
            vec3 reflectedRay=reflect(insideRay,exitNormal);
            float cosIn=abs(dot(insideRay,exitNormal));
            float discriminant=1.0-ior*ior*(1.0-cosIn*cosIn);
            float cosOut=sqrt(max(0.0,discriminant));
            float rs=(ior*cosIn-cosOut)/max(0.0001,ior*cosIn+cosOut);
            float rp=(ior*cosOut-cosIn)/max(0.0001,ior*cosOut+cosIn);
            float exitReflectance=discriminant<=0.0?1.0:0.5*(rs*rs+rp*rp);
            if(dot(studioRay,studioRay)<0.001)studioRay=reflectedRay;
            // Art-directed floor bounce: keep the upper clear dome bright against
            // the off-white page, with a darker returned ray at its base.
            ${water?`studioRay.y=-studioRay.y;reflectedRay.y=-reflectedRay.y;`:''}
            vec3 studioLight = mix(
              textureCubeUV(envMap, envMapRotation * studioRay, roughness).rgb,
              textureCubeUV(envMap, envMapRotation * reflectedRay, roughness).rgb,
              exitReflectance);
            float confidence=smoothstep(0.0,0.1,proxyDiscriminant/max(aa,0.00001));
            transmittedLight.rgb = mix(transmittedLight.rgb, studioLight, ${studioBlend}*confidence);
            transmittance = mix(transmittance, diffuseColor * volumeAttenuation(max(travel, thickness * 0.35), attenuationColor, attenuationDistance), confidence);
            }
          #endif
          vec3 attenuatedColor = transmittance * transmittedLight.rgb;
        `));
    };
  }else if(mat.name==='pouch_clear_film'){
    mat.transmission=0;mat.transparent=true;mat.opacity=.1;mat.depthWrite=false;
    mat.roughness=.08;mat.clearcoat=1;mat.clearcoatRoughness=.045;mat.color.set('#fffdf7');
    // Thin film contributes reflections without a milky diffuse veil. Keep
    // the filling in the normal pass; nested transmission loses gel interiors.
    mat.customProgramCacheKey=()=> 'clinging-film-v2';
    mat.onBeforeCompile=shader=>{
      shader.fragmentShader=shader.fragmentShader.replace('#include <opaque_fragment>',`
        vec3 filmReflection = totalSpecular;
        #ifdef USE_CLEARCOAT
          filmReflection += clearcoatSpecularDirect + clearcoatSpecularIndirect;
        #endif
        float filmEdge = pow(1.0 - abs(dot(normal, geometryViewDir)), 3.0);
        float filmHighlight = max(max(filmReflection.r, filmReflection.g), filmReflection.b);
        float filmAlpha = clamp(0.012 + filmEdge * 0.22 + filmHighlight * 0.9, 0.012, 0.78);
        // The film has no diffuse layer. Keep reflected highlights bright
        // through tone mapping, rather than tinting clear folds middle gray.
        gl_FragColor = vec4(vec3(1.7), filmAlpha);
      `);
    };
  }else if(mat.name==='beads_clear_skin'){
    // Keep the small inclusions sharp without refracting every particle.
    mat.color.set('#f8ffff');mat.transmission=0;mat.transparent=true;mat.opacity=.13;mat.depthWrite=false;
    mat.roughness=.1;mat.clearcoat=1;mat.ior=1.18;mat.thickness=.06;
    if(kind==='crunchball'&&!mobile){
      mat.transmission=1;mat.transparent=false;mat.opacity=1;mat.depthWrite=true;
      mat.roughness=.035;mat.ior=1.12;mat.thickness=.08;mat.clearcoatRoughness=.04;
    }
  }else if(mat.name==='jelly_tube_clear'){
    // Clear water tube: plain blending keeps the floaters inside vivid.
    mat.transmission=0;mat.transparent=true;mat.opacity=.42;mat.depthWrite=false;
    mat.roughness=.05;mat.clearcoat=1;mat.clearcoatRoughness=.03;mat.ior=1.33;
  }else if(mat.name==='foam_moodmallow'){
    // Pressure-reactive colour: a per-vertex memory of compression (moodStrain,
    // written by StrainColor) blends the surface toward the pressed hue.
    const moodColor={value:new Color(variantMood(kind,variant)??'#9b6fd8')};
    mat.userData.moodStrain=true;
    mat.customProgramCacheKey=()=> 'mood-strain-v1';
    mat.onBeforeCompile=shader=>{
      shader.uniforms.moodColor=moodColor;
      shader.vertexShader='attribute float moodStrain;\nvarying float vMoodStrain;\n'+shader.vertexShader.replace('#include <begin_vertex>','#include <begin_vertex>\n  vMoodStrain=moodStrain;');
      shader.fragmentShader='uniform vec3 moodColor;\nvarying float vMoodStrain;\n'+shader.fragmentShader.replace('#include <color_fragment>','#include <color_fragment>\n  diffuseColor.rgb=mix(diffuseColor.rgb,moodColor,smoothstep(0.0,1.0,vMoodStrain)*.92);');
    };
  }else if(mat.name==='jelly_globe_clear'){
    // Clear globe: plain blending keeps the snow and the bear inside crisp.
    mat.transmission=0;mat.transparent=true;mat.opacity=.2;mat.depthWrite=false;
    mat.roughness=.03;mat.clearcoat=1;mat.clearcoatRoughness=.02;mat.ior=1.33;
  }else if(mat.name==='jelly_lamp_clear'){
    // Clear lamp bottle: plain blending keeps the wax colours vivid.
    mat.transmission=0;mat.transparent=true;mat.opacity=.24;mat.depthWrite=false;
    mat.roughness=.03;mat.clearcoat=1;mat.clearcoatRoughness=.02;mat.ior=1.4;
  }else if(mat.name==='rind_mikan'){
    // Citrus rind: oil-gland dimples from a solid noise in rest space, applied
    // as screen-space-derivative bump mapping (Mikkelsen 2010), so the pores
    // stay put while a petal bends. Only the orange outer face is dimpled.
    mat.roughness=.55;mat.sheen=.25;mat.sheenColor=new Color('#ffd9a8');
    mat.customProgramCacheKey=()=> 'mikan-rind-v1';
    mat.onBeforeCompile=shader=>{
      shader.vertexShader='attribute vec3 restPosition;\nvarying vec3 vRindRest;\n'+shader.vertexShader.replace('#include <begin_vertex>','#include <begin_vertex>\n  vRindRest=restPosition;');
      shader.fragmentShader=`varying vec3 vRindRest;
        float rindHash(vec3 p){p=fract(p*.3183099+.1);p*=17.;return fract(p.x*p.y*p.z*(p.x+p.y+p.z));}
        float rindNoise(vec3 x){vec3 i=floor(x),f=fract(x);f=f*f*(3.-2.*f);
          return mix(mix(mix(rindHash(i),rindHash(i+vec3(1,0,0)),f.x),mix(rindHash(i+vec3(0,1,0)),rindHash(i+vec3(1,1,0)),f.x),f.y),
            mix(mix(rindHash(i+vec3(0,0,1)),rindHash(i+vec3(1,0,1)),f.x),mix(rindHash(i+vec3(0,1,1)),rindHash(i+vec3(1,1,1)),f.x),f.y),f.z);}
        vec3 rindPerturb(vec3 position,vec3 normal,vec2 dh,float side){
          vec3 sx=normalize(dFdx(position)),sy=normalize(dFdy(position)),r1=cross(sy,normal),r2=cross(normal,sx);
          float det=dot(sx,r1)*side;return normalize(abs(det)*normal-sign(det)*(dh.x*r1+dh.y*r2));}
      `+shader.fragmentShader.replace('#include <normal_fragment_maps>',`#include <normal_fragment_maps>
        #ifdef USE_COLOR
          float rindMask=smoothstep(.22,.5,vColor.r-vColor.b);
          if(rindMask>0.){
            float height=-smoothstep(.58,.9,rindNoise(vRindRest*30.))+.3*rindNoise(vRindRest*7.);
            normal=rindPerturb(-vViewPosition,normal,vec2(dFdx(height),dFdy(height))*rindMask*1.4,faceDirection);
          }
        #endif`);
    };
  }else if(mat.name==='jelly_balloon'){
    // Thin water-balloon rubber: glossy and a little see-through.
    mat.transmission=0;mat.transparent=true;mat.opacity=.88;mat.roughness=.12;mat.clearcoat=1;mat.clearcoatRoughness=.04;
  }else if(mat.name==='jelly_treasure'){
    // Cloudy jelly: plain blending so the hidden treasures show through with
    // their own haze, which depends on how much jelly lies in front of them.
    mat.transmission=0;mat.transparent=true;mat.opacity=.42;mat.depthWrite=false;mat.alphaTest=0;
    mat.color.set('#cfeeff');mat.roughness=.18;mat.clearcoat=1;mat.clearcoatRoughness=.06;mat.sheen=.4;mat.sheenColor=new Color('#ffffff');
  }else if(mat.name==='mochi_yaki'){
    mat.customProgramCacheKey=()=> 'mochi-toast-v1';
    mat.onBeforeCompile=shader=>mochiToastShader(shader);
  }else if(mat.name==='foam_honeytoast'){
    // Honey soaks in where it sits: a per-vertex amount (honeySoak) darkens the
    // crumb toward amber and makes it glossier.
    mat.customProgramCacheKey=()=> 'honey-soak-v1';
    mat.onBeforeCompile=shader=>{
      shader.vertexShader='attribute float honeySoak;\nvarying float vHoneySoak;\n'+shader.vertexShader.replace('#include <begin_vertex>','#include <begin_vertex>\n  vHoneySoak=honeySoak;');
      shader.fragmentShader='varying float vHoneySoak;\n'+shader.fragmentShader
        .replace('#include <color_fragment>','#include <color_fragment>\n  diffuseColor.rgb*=mix(vec3(1.),vec3(.80,.52,.22),smoothstep(0.,1.,vHoneySoak));')
        .replace('#include <roughnessmap_fragment>','#include <roughnessmap_fragment>\n  roughnessFactor=mix(roughnessFactor,.32,smoothstep(0.,1.,vHoneySoak));');
    };
  }else if(mat.name==='jelly_goldfish_clear'){
    // See-through water jelly: plain blending keeps the goldfish vivid; refraction washed it out.
    mat.transmission=0;mat.transparent=true;mat.opacity=.28;mat.depthWrite=false;
    mat.roughness=.04;mat.clearcoat=1;mat.clearcoatRoughness=.03;mat.ior=1.33;
  }else if(mat.name==='candy_shell_glass'){
    // Clear sugar glaze: shards overlap without hiding the seeds or the core.
    mat.transmission=0;mat.transparent=true;mat.opacity=.6;mat.depthWrite=false;
    mat.roughness=.04;mat.clearcoat=1;mat.clearcoatRoughness=.03;mat.ior=1.46;
  }else if(mat.name.startsWith('gelbead_')){
    // Layered tinted transparency lets nearby beads show through. Per-bead
    // refraction cannot see other transmissive beads in Three's opaque pass.
    mat.transmission=0;mat.transparent=true;mat.opacity=.8;mat.depthWrite=false;
    mat.roughness=.08;mat.clearcoat=1;mat.clearcoatRoughness=.05;mat.ior=1.33;
  }else if(JELLY[mat.name]){
    const look={...JELLY[mat.name],...(recolor?.color?{color:recolor.color}:{}),...(recolor?.absorption?{absorption:recolor.absorption}:{})};
    mat.color.set(look.color);mat.transmission=look.transmission;mat.opacity=1;
    mat.thickness=look.thickness;mat.attenuationColor.set(look.absorption);mat.attenuationDistance=1.35;
    mat.roughness=.13;mat.clearcoat=.5;mat.clearcoatRoughness=.08;
  }else if(mat.name==='bubble'){
    // Inclusions must enter the opaque pass to be visible through jelly.
    mat.transmission=0;mat.roughness=.08;mat.clearcoat=1;
  }else if(mat.transmission>0&&mat.transmission<.15&&!mat.name.startsWith('jelly_')){
    mat.transmission=0;
  }
  // Plain recolors (no translucent look table) set the base colour; vertex-coloured
  // toys multiply it as a tint.
  if(recolor?.base&&!JELLY[mat.name])mat.color.setRGB(...recolor.base);
  if(recolor?.filter)filterMaterial(mat,recolor.filter);
  return mat;
}
// Applies a rare colour's CSS filter to the final albedo (after vertex colours
// and textures), in sRGB like the thumbnail, keeping any earlier shader edits.
function filterMaterial(mat,css){
  const matrix=new Matrix3().fromArray(cssFilterMatrix(css)),before=mat.onBeforeCompile,key=mat.customProgramCacheKey;
  mat.onBeforeCompile=(shader,renderer)=>{
    before?.call(mat,shader,renderer);
    shader.uniforms.variantFilter={value:matrix};
    shader.fragmentShader='uniform mat3 variantFilter;\n'+shader.fragmentShader.replace('#include <color_fragment>','#include <color_fragment>\n  { vec3 s=pow(max(diffuseColor.rgb,0.),vec3(1./2.2));diffuseColor.rgb=pow(clamp(variantFilter*s,0.,1.),vec3(2.2)); }');
  };
  mat.customProgramCacheKey=()=>(key?.call(mat)??'')+'|filter:'+css;
}
